import React, { useState } from "react";
import { FaBox, FaDollarSign, FaToggleOn, FaToggleOff } from "react-icons/fa";

interface VariantDetailsProps {
  data: {
    variant_id: number;
    product_id: number;
    sku: string;
    variant_name: string;
    product_name: string;
    images: string[];
    attributes: Record<string, any>;
    price: number; // cents
    price_display: number; // formatted
    currency: string;
    compare_at_price: number | null;
    cost_price: number | null;
    barcode: string | null;
    weight_kg: number | null;
    stock: number;
    low_stock_threshold: number;
    is_default: number;
    status: string;
    is_active: number;
    sort_order: number;
    created_at: number;
    updated_at: number;
  };

  onSave?: (updated: any) => void;
}

export default function VariantDetails({ data, onSave }: VariantDetailsProps) {
  const [form, setForm] = useState({
    price_display: data.price_display,
    compare_at_price: data.compare_at_price ?? "",
    stock: data.stock,
    low_stock_threshold: data.low_stock_threshold,
    status: data.status,
    is_active: data.is_active === 1,
  });

  const [loading, setLoading] = useState(false);

  const isLowStock = form.stock <= form.low_stock_threshold;

  function handleChange(field: string, value: any) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setLoading(true);

    const payload = {
      variant_id: data.variant_id,
      price: Math.round(Number(form.price_display) * 100), // convert to cents
      compare_at_price: form.compare_at_price
        ? Math.round(Number(form.compare_at_price) * 100)
        : null,
      stock: Number(form.stock),
      low_stock_threshold: Number(form.low_stock_threshold),
      status: form.status,
      is_active: form.is_active ? 1 : 0,
    };

    if (onSave) await onSave(payload);

    setLoading(false);
  }

  return (
    <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-3xl">

      {/* ---------- Header ---------- */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold">
          {data.product_name} — {data.variant_name}
        </h2>
        <p className="text-sm text-gray-500">SKU: {data.sku}</p>
      </div>

      {/* ---------- Images ---------- */}
      {data.images?.length > 0 && (
        <div className="flex gap-4 mb-6 overflow-x-auto">
          {data.images.map((img, index) => (
            <img
              key={index}
              src={img}
              alt="variant"
              className="w-28 h-28 object-cover rounded-xl border"
            />
          ))}
        </div>
      )}

      {/* ---------- Price Section ---------- */}
      <div className="grid grid-cols-2 gap-4 mb-6">

        <div>
          <label className="text-sm text-gray-600">Price ({data.currency})</label>
          <div className="flex items-center border rounded-xl px-3 py-2">
            <FaDollarSign className="text-gray-400 mr-2" />
            <input
              type="number"
              value={form.price_display}
              onChange={(e) => handleChange("price_display", e.target.value)}
              className="w-full outline-none"
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-600">Compare At Price</label>
          <input
            type="number"
            value={form.compare_at_price}
            onChange={(e) => handleChange("compare_at_price", e.target.value)}
            className="w-full border rounded-xl px-3 py-2"
          />
        </div>
      </div>

      {/* ---------- Stock Section ---------- */}
      <div className="grid grid-cols-2 gap-4 mb-6">

        <div>
          <label className="text-sm text-gray-600">Stock</label>
          <div className="flex items-center border rounded-xl px-3 py-2">
            <FaBox className="text-gray-400 mr-2" />
            <input
              type="number"
              value={form.stock}
              onChange={(e) => handleChange("stock", e.target.value)}
              className="w-full outline-none"
            />
          </div>

          {isLowStock && (
            <p className="text-xs text-red-500 mt-1">
              Low stock warning ⚠ (threshold: {form.low_stock_threshold})
            </p>
          )}
        </div>

        <div>
          <label className="text-sm text-gray-600">Low Stock Threshold</label>
          <input
            type="number"
            value={form.low_stock_threshold}
            onChange={(e) =>
              handleChange("low_stock_threshold", e.target.value)
            }
            className="w-full border rounded-xl px-3 py-2"
          />
        </div>
      </div>

      {/* ---------- Availability ---------- */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <label className="text-sm text-gray-600">Status</label>
          <select
            value={form.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="ml-3 border rounded-lg px-3 py-1"
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <button
          onClick={() => handleChange("is_active", !form.is_active)}
          className="flex items-center gap-2"
        >
          {form.is_active ? (
            <>
              <FaToggleOn className="text-green-500 text-xl" />
              <span className="text-sm">Enabled</span>
            </>
          ) : (
            <>
              <FaToggleOff className="text-gray-400 text-xl" />
              <span className="text-sm">Disabled</span>
            </>
          )}
        </button>
      </div>

      {/* ---------- Save Button ---------- */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="w-full bg-black text-white py-3 rounded-xl hover:opacity-90 transition"
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
}